<template>
    <div class="map-left">
        <div ref="mapContainer" class="map"></div>

        <div v-if="isShowWin">
            <div class="searchWin" v-if="searchList.length">
                <div class="closeClass" @click="closeWin">
                    <i class="el-icon-circle-close"></i>
                </div>
                <div class="amap-pl-pc" v-for="(item, index) in searchList" :key="index"
                     @click="openMarkerTip(index, item)">
                    <div class="poibox">
                        <div class="amap_lib_placeSearch_poi poibox-icon"
                             :class="index === selectedIndex ? 'selected' : ''">
                            {{ index + 1 }}
                        </div>
                        <div class="poi-img" v-if="item.url"
                             :style="'background-image:url(' + item.url + '?operate=merge&amp;w=90&amp;h=56&amp;position=5)'"></div>
                        <h3 class="poi-title">
                            <span class="poi-name">{{ item.displayName }}</span>
                        </h3>
                        <div class="poi-info">
                            <p class="poi-addr">{{ $t('text.Address') }}：{{ item.formattedAddress }}</p>
                            <p class="poi-tel">{{ $t('text.Phone') }}：{{ item.tel }}</p>
                        </div>
                        <div class="clear"></div>
                    </div>
                </div>
            </div>
            <div class="searchWin" v-else>
                <div class="closeClass" @click="isShowWin = false">
                    <i class="el-icon-circle-close"></i>
                </div>
                <el-empty :description="$t('integration.no_data_temporarily')"></el-empty>
            </div>
        </div>

    </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import MapboxLanguage from '@mapbox/mapbox-gl-language'
import {
    marker_arch,
    marker_arch_active,
    marker_arch_error
} from '@/pages/home/config/mapIcon'

export default {
    props: {
        locationObj: {
            type: Object,
            default: () => ({ address: null, latitude: null, longitude: null, abnormal: false })
        },
        enableRepoint: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            flashInterval: null,
            mapInstance: null,
            marker: null,
            defaultLocation: { longitude: -123.1192, latitude: 49.2638 },
            defaultZoom: 4,
            isShowWin: false,
            searchList: [],
            searchMarkerList: [],
            selectedIndex: -1,
            addressSearch: '',
            searchLoading: false
        }
    },
    watch: {
        locationObj: {
            handler(newVal) {
                if (newVal.latitude !== null && newVal.longitude !== null && this.mapInstance) {
                    console.log(newVal)
                    this.updateMainMarker(newVal, true)
                    this.mapInstance.setZoom(16)
                }
            },
            deep: true
        },
        enableRepoint(newVal) {
            if (newVal) {
                if (this.locationObj) {
                    this.clearFlashInterval()
                    this.updateMainMarker(this.locationObj)
                }
            } else {
                if (this.locationObj) {
                    this.updateMainMarker(this.locationObj)
                }
            }
        }
    },
    async mounted() {
        this.createMap()
    },
    beforeDestroy() {
        if (this.flashInterval) {
            clearInterval(this.flashInterval)
            this.flashInterval = null
        }
    },
    methods: {
        getMapboxLanguage() {
            const lang = this.$i18n.locale || 'zh_CN'
            if (lang === 'zh_CN') return 'zh-Hans'
            if (lang === 'en_US') return 'en'
            return 'en'
        },
        createMap() {
            mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN
            mapboxgl.telemetryEnabled = false
            const language = this.getMapboxLanguage()

            this.mapInstance = new mapboxgl.Map({
                container: this.$refs.mapContainer,
                style: 'mapbox://styles/mapbox/streets-v12',
                center: [0, 0],
                zoom: 16,
                language: language,
                attributionControl: false
            })

            const languagePlugin = new MapboxLanguage({
                defaultLanguage: language
            })
            this.mapInstance.addControl(languagePlugin)

            this.mapInstance.on('click', async (e) => {
                if (!this.enableRepoint) return
                const lng = e.lngLat.lng
                const lat = e.lngLat.lat
                const pos = { latitude: lat, longitude: lng, address: '' }

                this.updateMainMarker({ ...pos, abnormal: false })


                try {
                    const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${process.env.VUE_APP_MAPBOX_TOKEN}&limit=1`)
                    const data = await res.json()
                    if (data.features && data.features.length) {
                        pos.address = data.features[0].place_name
                        this.$emit('update:location', pos)
                    }else {
                      this.$emit('update:location', pos)
                    }
                } catch (err) {
                  this.$emit('update:location', pos)
                    console.error('Mapbox reverse geocoding error:', err)
                }
            })

            if (!this.locationObj || this.locationObj.latitude == null || this.locationObj.longitude == null) {
                this.locateBrowser()
            } else {
                this.updateMainMarker(this.locationObj, true)
            }
        },
        locateBrowser() {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    position => {
                        const coords = {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                            abnormal: true
                        }
                        this.updateMainMarker(coords, true)
                        this.$emit('update:location', coords)
                    },
                    error => {
                        console.warn('浏览器定位失败，使用默认位置', error)
                        this.applyDefaultLocation()
                    }
                )
            } else {
                console.warn('浏览器不支持地理定位，使用默认位置')
                this.applyDefaultLocation()
            }
        },
        applyDefaultLocation() {
            const def = this.defaultLocation
            const coords = { latitude: def.latitude, longitude: def.longitude, abnormal: false }
            this.updateMainMarker(coords, true)
            this.mapInstance.setZoom(this.defaultZoom)
            this.$emit('update:location', coords)
        },
        updateMainMarker({ latitude, longitude, abnormal }, center = false) {
            if (this.marker) {
                this.marker.remove()
                this.marker = null
            }
            if (this.flashInterval) {
                clearInterval(this.flashInterval)
                this.flashInterval = null
            }
            if (latitude != null && longitude != null && this.mapInstance) {
                const el = this.htmlToElement(marker_arch)
                this.marker = new mapboxgl.Marker(el)
                    .setLngLat([longitude, latitude])
                    .addTo(this.mapInstance)

                if (center) {
                    this.mapInstance.setCenter([longitude, latitude])
                }
                if (!this.enableRepoint && abnormal) {
                    this.startFlashing()
                }
            }
        },
        updateSearchMarker(searchList) {
            if (this.searchMarkerList) {
                this.searchMarkerList.forEach(marker => marker.remove())
                this.searchMarkerList = []
            }
            if (searchList && searchList.length) {
                const bounds = new mapboxgl.LngLatBounds()
                searchList.map((item, index) => {
                    const html = `<div class="amap_lib_placeSearch_poi poibox-icon">${index + 1}</div>`
                    const tpl = document.createElement('template')
                    tpl.innerHTML = html.trim()
                    const content = tpl.content.firstChild
                    const marker = new mapboxgl.Marker(content)
                        .setLngLat([item.longitude, item.latitude])
                        .addTo(this.mapInstance)

                    marker.getElement().addEventListener('click', () => {
                        this.openSearchMarkerInfo(marker, item)
                    })

                    this.searchMarkerList.push(marker)
                    bounds.extend([item.longitude, item.latitude])
                })
                this.mapInstance.fitBounds(bounds)
            }
        },
        openSearchMarkerInfo(marker, item) {
            const addressLabel = this.$t('text.Address')
            const popup = new mapboxgl.Popup()
                .setHTML(`
                    <div>
                        <h1 style="font-size:16px; margin:0; color:#333">${item.displayName}</h1>
                        <h2 style="font-size:14px; margin:4px 0 0; color:#666">${addressLabel}: ${item.simpleAddress || ''}</h2>
                    </div>
                `)
                .addTo(this.mapInstance)

            const pos = { latitude: item.latitude, longitude: item.longitude, address: item.simpleAddress }
            this.$emit('update:location', pos)
        },
        htmlToElement(html) {
            const div = document.createElement('div')
            div.innerHTML = html
            const el = div.firstElementChild
            el.style.cursor = 'pointer'
            return el
        },
        startFlashing() {
            let visible = true
            this.flashInterval = setInterval(() => {
                if (this.marker) {
                    visible = !visible
                    const newContent = this.htmlToElement(visible ? marker_arch_error : marker_arch)
                    const markerEl = this.marker.getElement()
                    markerEl.innerHTML = newContent.innerHTML
                }
            }, 500)
        },
        async searchAddress(address) {
            if (!address) return

            this.isShowWin = true

            if (this.addressSearch === address) {
                this.searchMarkerList.forEach(item => {
                    const el = item.getElement()
                    el.style.display = 'block'
                })
                return
            }

            this.addressSearch = address
            this.searchLoading = true

            try {
                const response = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${process.env.VUE_APP_MAPBOX_TOKEN}&limit=10`)
                const data = await response.json()

                if (data.features && data.features.length) {
                    const simple = data.features.map(r => ({
                        id: r.id,
                        displayName: r.text,
                        formattedAddress: r.place_name,
                        simpleAddress: r.place_name,
                        latitude: r.center[1],
                        longitude: r.center[0]
                    }))

                    this.searchList = simple
                    this.$emit('searchResult', simple)
                } else {
                    this.searchList = []
                    this.$emit('searchResult', [])
                }
            } catch (e) {
                console.error('Mapbox geocoding error:', e)
                this.searchList = []
                this.$emit('searchResult', [])
            }
            this.updateSearchMarker(this.searchList)
            this.searchLoading = false
        },
        clearFlashInterval() {
            if (this.flashInterval) {
                clearInterval(this.flashInterval)
                this.flashInterval = null
            }
        },
        parseAddressParts(address) {
            const result = {
                mainAddress: '',
                plusCode: '',
                postalCode: ''
            }

            address = address.trim()

            const postalCodeRegex = /(邮政编码[:：]?\s*|Postal\s*Code[:：]?\s*)(\d{6})/i
            const postalMatch = address.match(postalCodeRegex)
            if (postalMatch) {
                result.postalCode = postalMatch[2]
                address = address.replace(postalMatch[0], '').trim()
            }

            const plusCodeRegex = /\b([23456789CFGHJMPQRVWX]{4,6}\+[23456789CFGHJMPQRVWX]{2,4})\b/
            const plusMatch = address.match(plusCodeRegex)
            if (plusMatch) {
                result.plusCode = plusMatch[1]
                address = address.replace(plusMatch[0], '').trim()
            }

            result.mainAddress = address

            return result
        },
        openMarkerTip(index, searchItem) {
            this.selectedIndex = index
            const marker = this.searchMarkerList[index]
            if (marker) {
                this.openSearchMarkerInfo(marker, searchItem)
            }
        },
        closeWin() {
            this.isShowWin = false
            this.searchMarkerList.forEach(item => {
                const el = item.getElement()
                el.style.display = 'none'
            })
        }
    }
}
</script>

<style>
.poibox-icon {
    margin-left: 7px;
    margin-top: 4px;
}
</style>

<style scoped lang="scss">
.map-left {
    flex: 1;
    padding: 10px;
    box-sizing: border-box;
    color: transparent;
    border: 1px solid #ccc;
    width: 100%;
    height: 100%;
    position: relative;
}

.map {
    width: 100%;
    height: 100%;
}

.searchWin {
    position: absolute;
    top: 10px;
    bottom: 10px;
    right: 10px;
    width: 300px;
    overflow-y: auto;
    background: rgba($color: #ffffff, $alpha: 0.7);

    .poibox {
        border-bottom: 1px solid #eaeaea;
        cursor: pointer;
        padding: 5px 0 5px 10px;
        position: relative;
        min-height: 35px;

        .poi-title {
            margin-left: 25px;
            font-size: 13px;
            overflow: hidden;
        }

        .poi-info {
            word-break: break-all;
            margin: 0 0 0 25px;
            overflow: hidden;
        }

        .poi-info p {
            color: #999;
            font-family: Tahoma;
            line-height: 20px;
            font-size: 12px;
        }

        .poibox-icon {
            margin-left: 7px;
            margin-top: 4px;
        }

        &:hover {
            background: #f6f6f6;
        }
    }

    .closeClass {
        text-align: right;

        i {
            text-align: right;
            font-size: 16px;
            font-weight: bold;
            cursor: pointer;
            margin: 10px;

            &:hover {
                color: red;
            }
        }
    }
}
</style>
